import type { JSX } from 'react';
import { Controller } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { useScopes } from '../chat/use-chat-data';
import { fieldLabel, input, segment } from '../shell/look';
import type { EditorForm } from './schedule-editor';
import {
  FREQUENCIES,
  FREQUENCY_LABELS,
  HOURS,
  MINUTES,
  MONTH_DAYS,
  WEEKDAY_CHIPS,
  toggled,
} from './schedule-form';

const chip = (on: boolean): string =>
  cn(
    'rounded-full border px-3 py-1.5 text-[12.5px] transition-colors',
    on ? 'border-acc bg-acc-soft text-acc' : 'border-line text-mute hover:text-ink bg-transparent',
  );

/** The name, the prompt as tested in chat, and the sources it asks. */
export function WhatFields({ form }: { form: EditorForm }): JSX.Element {
  const scopes = useScopes();
  return (
    <>
      <label className={fieldLabel}>
        Name
        <input {...form.register('name')} placeholder="Daily gross" className={input} />
      </label>
      <label className={fieldLabel}>
        Prompt
        <textarea
          {...form.register('prompt')}
          rows={3}
          placeholder="What was the gross yesterday?"
          className={cn(input, 'resize-y leading-normal')}
        />
      </label>
      <Controller
        control={form.control}
        name="scope"
        render={({ field }) => (
          <div className={fieldLabel} role="group" aria-label="Search in">
            Search in
            <div className="flex flex-wrap gap-1.5">
              {(scopes.data ?? []).map((s) => (
                <button
                  key={s.option}
                  type="button"
                  disabled={!s.available}
                  aria-pressed={field.value.includes(s.option)}
                  onClick={() => field.onChange(toggled(field.value, s.option))}
                  className={cn(chip(field.value.includes(s.option)), 'disabled:opacity-40')}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      />
    </>
  );
}

/** How often, on which days, at what time, in which zone. */
export function WhenFields({
  form,
  timezones,
}: {
  form: EditorForm;
  timezones: readonly string[];
}): JSX.Element {
  const frequency = form.watch('frequency');
  return (
    <>
      <div className={fieldLabel}>
        Repeat
        <div className={cn(segment.group, 'bg-surf2 w-fit')} role="radiogroup" aria-label="Repeat">
          {FREQUENCIES.map((f) => (
            <button
              key={f}
              type="button"
              role="radio"
              aria-checked={frequency === f}
              onClick={() => form.setValue('frequency', f, { shouldDirty: true })}
              className={frequency === f ? segment.on : segment.off}
            >
              {FREQUENCY_LABELS[f]}
            </button>
          ))}
        </div>
      </div>
      {frequency === 'weekly' && <DayChips form={form} />}
      <TimeFields form={form} timezones={timezones} monthly={frequency === 'monthly'} />
      {frequency === 'monthly' && (
        <p className="text-mute m-0 text-xs">Days 29–31 run on the last day of shorter months.</p>
      )}
    </>
  );
}

function DayChips({ form }: { form: EditorForm }): JSX.Element {
  return (
    <Controller
      control={form.control}
      name="days"
      render={({ field }) => (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Days of the week">
          {WEEKDAY_CHIPS.map((d) => (
            <button
              key={d.value}
              type="button"
              aria-pressed={field.value.includes(d.value)}
              onClick={() => field.onChange(toggled(field.value, d.value))}
              className={chip(field.value.includes(d.value))}
            >
              {d.label}
            </button>
          ))}
        </div>
      )}
    />
  );
}

const select = cn(input, 'font-mono text-[13.5px]');

function Options({ values }: { values: readonly string[] }): JSX.Element {
  return (
    <>
      {values.map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </>
  );
}

/** Day of month (monthly only), hour, minute and zone: plain selects, one per value. */
function TimeFields({
  form,
  timezones,
  monthly,
}: {
  form: EditorForm;
  timezones: readonly string[];
  monthly: boolean;
}): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-3 tab:grid-cols-4">
      {monthly && (
        <label className={fieldLabel}>
          Day
          <select {...form.register('dayOfMonth')} className={select}>
            <Options values={MONTH_DAYS} />
          </select>
        </label>
      )}
      <label className={fieldLabel}>
        Hour
        <select {...form.register('hour')} className={select}>
          <Options values={HOURS} />
        </select>
      </label>
      <label className={fieldLabel}>
        Minute
        <select {...form.register('minute')} className={select}>
          <Options values={MINUTES} />
        </select>
      </label>
      <label className={cn(fieldLabel, 'col-span-2')}>
        Timezone
        <select {...form.register('timezone')} className={select}>
          <Options values={timezones} />
        </select>
      </label>
    </div>
  );
}
